import './App.css';
import * as Sentry from '@sentry/react';
import { FC, useEffect } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';

import DashboardLayout from '@/components/layout/DashboardLayout.tsx';
import SignOut from '@/components/layout/signOutPage/SignOut.tsx';
import AuthenticatedRoutes from '@/components/router/AuthenticatedRoutes.tsx';
import HomeRoute from '@/components/router/HomeRoute.tsx';
import UnauthenticatedRoutes from '@/components/router/UnauthenticatedRoutes.tsx';
import { config } from '@/config/config.ts';
import ChangePassword from '@/pages/ChangePasswordPage';
import ClaimDetailsPage from '@/pages/ClaimDetailsPage.tsx';
import ClaimsDashboard from '@/pages/ClaimsDashboard';
import CorporateRegisterPage from '@/pages/CorporateRegisterPage.tsx';
import CorporateUsersDashboard from '@/pages/CorporateUsersDashboard.tsx';
import CreateJoiningLink from '@/pages/CreateJoiningLinkPage.tsx';
import DeleteAccountPage from '@/pages/DeleteAccountPage.tsx';
import ForgotPassword from '@/pages/ForgotPasswordPage';
import GolfCourseDashboard from '@/pages/GolfCourseDashboard.tsx';
import LoginPage from '@/pages/LoginPage.tsx';
import MagicLinkActivationPage from '@/pages/MagicLinkActivationPage.tsx';
import NotFoundPage from '@/pages/NotFoundPage.tsx';
import PaymentCallbackPage from '@/pages/PaymentCallbackPage.tsx';
import PaymentFailedPage from '@/pages/PaymentFailedPage.tsx';
import PaymentPage from '@/pages/PaymentPage.tsx';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage.tsx';
import PurchaseVouchersPage from '@/pages/PurchaseVouchersPage.tsx';
import ResetPassword from '@/pages/ResetPasswordPage';
import RewardTierDashboard from '@/pages/RewardTierDashboard.tsx';
import SentryTest from '@/pages/SentryTest.tsx';
import SupportPage from '@/pages/SupportPage.tsx';
import VerifyAccountPage from '@/pages/VerifyAccountPage.tsx';
import VouchersDashboard from '@/pages/VouchersDashboard.tsx';
import { useAuthStore } from '@/stores/useAuthStore.ts';

const App: FC = () => {
  const { initialize, isInitialized, userId } = useAuthStore();

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (userId) {
      Sentry.setUser({ id: userId });
    } else {
      Sentry.setUser(null);
    }
  }, [userId]);

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <BrowserRouter>
      <ToastContainer />
      <Routes>
        <Route path="*" element={<NotFoundPage />} />
        {config.appEnv !== 'production' && (
          <Route path="/sentry-test" element={<SentryTest />} />
        )}

        <Route path="/corporate-sign-up" element={<CorporateRegisterPage />} />

        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/verify-account" element={<VerifyAccountPage />} />

        <Route element={<UnauthenticatedRoutes />}>
          <Route path="/sign-in" element={<LoginPage />} />
          <Route path="/sign-in/magic" element={<MagicLinkActivationPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

        <Route element={<AuthenticatedRoutes />}>
          <Route path="/sign-out" element={<SignOut />} />
        </Route>

        <Route element={<DashboardLayout />}>
          <Route element={<AuthenticatedRoutes />}>
            <Route path="/" element={<HomeRoute />} />
            <Route path="change-password" element={<ChangePassword />} />
            <Route path="vouchers" element={<VouchersDashboard />} />
          </Route>

          <Route element={<AuthenticatedRoutes allowedRoles={['corporate']} />}>
            <Route
              path="purchase-vouchers"
              element={<PurchaseVouchersPage />}
            />
            <Route path="payment" element={<PaymentPage />} />
            <Route path="payment/success" element={<PaymentSuccessPage />} />
            <Route path="payment/cancelled" element={<PaymentFailedPage />} />
            <Route path="payment/callback" element={<PaymentCallbackPage />} />
          </Route>

          <Route element={<AuthenticatedRoutes allowedRoles={['admin']} />}>
            <Route path="golf-course" element={<GolfCourseDashboard />} />
            <Route path="reward-tier" element={<RewardTierDashboard />} />
            <Route path="claims-list" element={<ClaimsDashboard />} />
            <Route path="claim-details" element={<ClaimDetailsPage />} />
            <Route path="vouchers" element={<VouchersDashboard />} />
            <Route
              path="corporate-users"
              element={<CorporateUsersDashboard />}
            />
            <Route
              path="invite-corporate-user"
              element={<CreateJoiningLink />}
            />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default App;
